/**
 * 写入前检查"这个值真的存得住吗"。
 *
 * 为什么要在写入前查一遍，而不是让 IndexedDB 自己去抛 `DataCloneError`：
 *
 * - **静默数据丢失**。`{ pids: [() => 1] }` 这类值如果被浅拷贝或 JSON 化后写入，
 *   读回来会变成 `{}` 或 `undefined`，而写入过程不会报任何错。等到用户发现"上次保存的参数
 *   缺了一半"时，原始值早就没了。宁可写不进去并当场报错，也不能写进去一半。
 * - **跨实现一致性**。内存实现用 `structuredClone`，IndexedDB 用自己的克隆算法；两者对
 *   边角值（`undefined`、`NaN`、循环引用）的处理不完全一致。把判定集中在一个函数里，
 *   才不会出现"测试用内存实现全绿、上线换 IndexedDB 就挂"这种最难查的偏差。
 *
 * 规则是白名单而不是黑名单：只有明确知道结构化克隆能原样还原的类型才放行。
 */

import { NotStorableError } from './errors';

/** 与 `Object.prototype`（或 `null` 原型）不同的对象都已经不是"纯数据"了 */
function isPlainObject(value: object): boolean {
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isTypedArray(value: object): boolean {
  return ArrayBuffer.isView(value);
}

function typeNameOf(value: object): string {
  const ctor = (value as { constructor?: { name?: unknown } }).constructor;
  const name = ctor?.name;
  return typeof name === 'string' && name.length > 0 ? name : '对象';
}

interface CheckState {
  readonly seen: WeakSet<object>;
  /** 命中的第一个问题；只报第一个，避免一个坏对象刷出几十条同样的错误 */
  failure: { readonly path: string; readonly reason: string } | null;
}

function fail(state: CheckState, path: string, reason: string): void {
  state.failure ??= { path, reason };
}

function walk(value: unknown, path: string, state: CheckState): void {
  if (state.failure !== null) return;

  switch (typeof value) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'bigint':
      // NaN / ±Infinity 结构化克隆能原样保留，不需要特别拒绝（拒绝它们必须先定义"用什么代替"）
      return;
    case 'undefined':
      // 对象/数组里的 undefined 会被结构化克隆保留；顶层 undefined 见 assertStorable
      return;
    case 'symbol':
      fail(state, path, 'Symbol 不是可结构化克隆的值');
      return;
    case 'function':
      fail(state, path, '函数不是可结构化克隆的值（克隆后读回来会变成 undefined）');
      return;
    default:
      break;
  }

  if (value === null) return;
  const object = value as object;
  // 循环引用结构化克隆支持，这里只用来防止无限递归；不报错
  if (state.seen.has(object)) return;
  state.seen.add(object);

  if (value instanceof Date) return;
  if (value instanceof RegExp) return;
  if (value instanceof ArrayBuffer) return;
  if (isTypedArray(object)) return;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return;

  if (value instanceof Map) {
    let index = 0;
    for (const [key, entry] of value) {
      walk(key, `${path}<键 ${index}>`, state);
      walk(entry, `${path}[${String(key)}]`, state);
      index += 1;
    }
    return;
  }

  if (value instanceof Set) {
    let index = 0;
    for (const entry of value) {
      walk(entry, `${path}[${index}]`, state);
      index += 1;
    }
    return;
  }

  if (value instanceof WeakMap || value instanceof WeakSet) {
    fail(state, path, 'WeakMap / WeakSet 不是可结构化克隆的值');
    return;
  }

  if (value instanceof Promise) {
    fail(state, path, 'Promise 不是可结构化克隆的值（存进去读回来会是一个空对象）');
    return;
  }

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      walk(value[index], `${path}[${index}]`, state);
      if (state.failure !== null) return;
    }
    return;
  }

  if (!isPlainObject(object)) {
    fail(
      state,
      path,
      `${typeNameOf(object)} 的实例不是纯数据：结构化克隆会丢掉原型与方法，读回来只剩数据字段，属于静默降级`,
    );
    return;
  }

  for (const [key, entry] of Object.entries(object)) {
    walk(entry, `${path}.${key}`, state);
    if (state.failure !== null) return;
  }
}

/**
 * 值是否可持久化。不可持久化时抛 {@link NotStorableError}（而不是返回 false）：
 * 调用方几乎没有"知道不能存还继续"的合理场景，返回值只会被忽略。
 */
export function assertStorable(value: unknown, path = 'value'): void {
  if (value === undefined) {
    throw new NotStorableError(
      path,
      '顶层不能是 undefined：「没有这条记录」和「记录的值是 undefined」在读取时无法区分',
    );
  }
  const state: CheckState = { seen: new WeakSet(), failure: null };
  walk(value, path, state);
  if (state.failure !== null) throw new NotStorableError(state.failure.path, state.failure.reason);
}

const encoder = typeof TextEncoder === 'function' ? new TextEncoder() : null;

function stringBytes(value: string): number {
  if (encoder === null) return value.length * 2;
  return encoder.encode(value).length;
}

interface SizeState {
  readonly seen: WeakSet<object>;
  bytes: number;
}

/**
 * 估算一条记录的字节数，用于配额预算与审计日志轮转。
 *
 * 这是**估算**：结构化克隆的实际落盘体积各家实现不同（V8 会做字符串驻留与数字压缩），
 * 我们只需要一个稳定的上界来触发轮转，不需要精确到字节。字符串按 UTF-8 计，
 * 对象与数组各加固定开销，循环引用只算一次。
 */
export function estimateStorableBytes(value: unknown): number {
  const state: SizeState = { seen: new WeakSet(), bytes: 0 };
  measure(value, state);
  return state.bytes;
}

function measure(value: unknown, state: SizeState): void {
  switch (typeof value) {
    case 'boolean':
      state.bytes += 1;
      return;
    case 'number':
      state.bytes += 8;
      return;
    case 'bigint':
      state.bytes += 16;
      return;
    case 'string':
      state.bytes += stringBytes(value) + 2;
      return;
    case 'undefined':
      state.bytes += 4;
      return;
    case 'symbol':
    case 'function':
      state.bytes += 8;
      return;
    default:
      break;
  }

  if (value === null) {
    state.bytes += 4;
    return;
  }

  const object = value as object;
  if (state.seen.has(object)) return;
  state.seen.add(object);

  if (value instanceof Date) {
    state.bytes += 8;
    return;
  }
  if (value instanceof RegExp) {
    state.bytes += 16;
    return;
  }
  if (value instanceof ArrayBuffer) {
    state.bytes += value.byteLength + 4;
    return;
  }
  if (isTypedArray(object)) {
    state.bytes += (value as ArrayBufferView).byteLength + 16;
    return;
  }
  if (value instanceof Map) {
    state.bytes += 8;
    for (const [key, entry] of value) {
      measure(key, state);
      measure(entry, state);
    }
    return;
  }
  if (value instanceof Set) {
    state.bytes += 8;
    for (const entry of value) measure(entry, state);
    return;
  }
  if (Array.isArray(value)) {
    state.bytes += 16;
    for (const entry of value) measure(entry, state);
    return;
  }
  state.bytes += 16;
  for (const [key, entry] of Object.entries(object)) {
    state.bytes += stringBytes(key) + 8;
    measure(entry, state);
  }
}
